/**
 * Google Analytics (GA4) — mirrors Angular `google.analytics.ts`.
 *
 * Tracking is limited to two hosts: `cornerstone.libertyrent.com` (production id)
 * and `cornerstage.libertyrent.com` (stage id). Anywhere else — local, dev,
 * preprod, the `v2-*` hosts — every helper here is a no-op, and `index.html`
 * skips loading gtag.js entirely. Keep both host lists in sync.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

export const GA_ID_STAGE = 'G-SSK09B6PVC'
export const GA_ID_PROD = 'G-0GW29BP41X'

export const GA_PROD_HOST = 'cornerstone.libertyrent.com'
export const GA_STAGE_HOST = 'cornerstage.libertyrent.com'

function isDev(): boolean {
  return import.meta.env.DEV
}

function canTrack(): boolean {
  return (
    !isDev() &&
    typeof window !== 'undefined' &&
    getAnalyticsId() !== '' &&
    typeof window.gtag === 'function'
  )
}

/** GA4 measurement id for the current host, or `''` on untracked hosts. */
export function getAnalyticsId(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (hostname === GA_PROD_HOST) return import.meta.env.VITE_GA_ID_PROD || GA_ID_PROD
  if (hostname === GA_STAGE_HOST) return import.meta.env.VITE_GA_ID_STAGE || GA_ID_STAGE
  return ''
}

function readStoredUserId(): string | undefined {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as { user_id?: string | number }
    return parsed?.user_id != null ? String(parsed.user_id) : undefined
  } catch {
    return undefined
  }
}

export function setAnalyticsUser(): void {
  if (!canTrack()) return
  window.gtag!('set', { cookie_flags: 'SameSite=None;Secure' })
  const userId = readStoredUserId()
  window.gtag!('config', getAnalyticsId(), userId ? { user_id: userId } : {})
}

export function setScreenView(screenName: string): void {
  if (!canTrack()) return
  const appName = import.meta.env.VITE_APP_NAME || 'Liberty Rent'
  window.gtag!('event', 'screen_view', { app_name: appName, screen_name: screenName })
}

/**
 * Send a Google Analytics event.
 * @param eventAction - Event action (e.g. 'autosave', 'click')
 * @param eventCategory - Event category (e.g. 'resident application info')
 * @param eventLabel - Event label (e.g. applicant ID)
 */
export function sendAnalytics(
  eventAction?: string,
  eventCategory?: string,
  eventLabel?: string | number | null,
): void {
  if (!canTrack()) return
  window.gtag!('event', eventAction, {
    event_category: eventCategory,
    event_label: String(eventLabel ?? ''),
  })
}

export function sendPageLoadingTiming(
  eventCategory?: string,
  name?: string,
  value?: number,
): void {
  if (!canTrack()) return
  window.gtag!('event', 'timing_complete', {
    event_category: eventCategory,
    name,
    value,
  })
}

export function sendException(description: string): void {
  if (!canTrack()) return
  window.gtag!('event', 'exception', { description, fatal: false })
}

/** Last URL segment, first letter capitalized — Angular AppService parity. */
export function screenNameFromPath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  const segment = trimmed.substring(trimmed.lastIndexOf('/') + 1)
  if (!segment) return ''
  return segment.charAt(0).toUpperCase() + segment.slice(1)
}
